import React, {useMemo, useState, useRef} from 'react';
import {View, Text, TouchableOpacity, Image, Platform, Modal, Dimensions} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, borderRadius, shadows, typography} from '~constants/design';
import {haptics} from '~Utils/haptics';
import {ChevronIcon, LogoIcon, LogoIconBar, SettingsIcon} from '~assets/icons';
import Ionicons from 'react-native-vector-icons/Ionicons';

interface ModernChatHeaderProps {
  userName: string;
  userImage?: string;
  isOnline?: boolean;
  lastSeen?: string;
  issueNumber?: string;
  onBackPress?: () => void;
  onUserPress?: () => void;
  onCallPress?: () => void;
  onVideoPress?: () => void;
  onMorePress?: () => void;
  showCallButtons?: boolean;
  showMoreButton?: boolean;
  showBlockButton?: boolean;
  isUserBlocked?: boolean;
  onBlockPress?: () => void;
  chatWIthUser?: boolean;
  showLeaveGroupButton?: boolean;
  onLeaveGroupPress?: () => void;
}

const ModernChatHeader: React.FC<ModernChatHeaderProps> = ({
  userName,
  userImage,
  isOnline = false,
  lastSeen,
  issueNumber,
  onBackPress,
  onUserPress,
  onCallPress,
  onVideoPress,
  onMorePress,
  showCallButtons = false,
  showMoreButton = false,
  showBlockButton = false,
  isUserBlocked = false,
  onBlockPress,
  chatWIthUser,
  showLeaveGroupButton = false,
  onLeaveGroupPress,
}) => {
  const {colors} = useTheme();
  const {top} = useSafeAreaInsets();
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({x: 0, y: 0});
  const menuButtonRef = useRef<TouchableOpacity>(null);

  const handleBackPress = () => {
    if (onBackPress) {
      haptics.medium();
      onBackPress();
    }
  };

  const handleUserPress = () => {
    if (onUserPress) {
      haptics.medium();
      onUserPress();
    }
  };

  const handleMenuPress = () => {
    if (menuButtonRef.current) {
      menuButtonRef.current.measure((fx, fy, width, height, px, py) => {
        const screenWidth = Dimensions.get('window').width;
        const menuWidth = 150;
        const rightMargin = 16;
        
        setMenuPosition({
          x: Math.min(px, screenWidth - menuWidth - rightMargin),
          y: py + height + 5,
        });
        setShowMenu(true);
        haptics.medium();
      });
    }
  };

  const handleBlockPress = () => {
    setShowMenu(false);
    if (onBlockPress) {
      haptics.medium();
      onBlockPress();
    }
  };

  const renderUserAvatar = useMemo(() => {
    if (!userImage) {
      return (
        <View
          style={{
            height: 40,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <LogoIcon color={colors.primaryDark} />
        </View>
      );
    }

    return (
      <View style={{position: 'relative'}}>
        <Image
          source={{uri: userImage}}
          style={{
            width: 40,
            height: 40,
            borderRadius: borderRadius.full,
            ...shadows.sm,
          }}
          resizeMode="cover"
        />
        {isOnline && (
          <View
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 12,
              height: 12,
              borderRadius: borderRadius.full,
              backgroundColor: colors.success,
              borderWidth: 2,
              borderColor: colors.surface,
            }}
          />
        )}
      </View>
    );
  }, [userImage, isOnline, colors]);

  const getStatusText = () => {
    if (isOnline) {
      return 'Online';
    }
    if (lastSeen) {
      return `Last seen ${lastSeen}`;
    }
    return '';
  };

  const renderActionButton = (icon: React.ReactNode, onPress?: () => void, disabled = false) => (
    <TouchableOpacity
      style={{
        width: 40,
        height: 40,
        borderRadius: borderRadius.full,
        backgroundColor: colors.gray100,
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: spacing.sm,
        opacity: disabled ? 0.5 : 1,
      }}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.7}>
      {icon}
    </TouchableOpacity>
  );

  return (
    <View
      style={{
        backgroundColor: colors.surface,
        paddingTop: Platform.OS === 'ios' ? top : top + spacing.md,
        paddingBottom: spacing.md,
        paddingHorizontal: spacing.lg,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        ...shadows.sm,
      }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
        {/* Left Section - Back Button (if provided) & User Info */}
        <View style={{flexDirection: 'row', alignItems: 'center', flex: 1}}>
          {onBackPress && (
            <TouchableOpacity
              style={{
                width: 40,
                height: 40,
                borderRadius: borderRadius.full,
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: spacing.sm,
              }}
              onPress={handleBackPress}
              activeOpacity={0.7}>
              <ChevronIcon />
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
              paddingLeft: onBackPress ? 0 : spacing.lg, // Add padding when no back button
            }}
            onPress={handleUserPress}
            disabled={!onUserPress}
            activeOpacity={0.7}>
            {renderUserAvatar}

            <View style={{marginLeft: spacing.md, flex: 1}}>
              <View style={{flexDirection: 'row', alignItems: 'center'}}>
                <Text
                  style={{
                    fontSize: typography.fontSize.lg,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                    flex: 1,
                  }}
                  numberOfLines={1}>
                  {chatWIthUser && 'Chat with '}{userName}
                </Text>
                {issueNumber && false && (
                  <Text
                    style={{
                      fontSize: typography.fontSize.sm,
                      color: colors.textSecondary,
                      fontWeight: typography.fontWeight.medium,
                      marginLeft: spacing.xs,
                    }}>
                    #{issueNumber}
                  </Text>
                )}
              </View>

              {getStatusText() && (
                <Text
                  style={{
                    fontSize: typography.fontSize.sm,
                    color: isOnline ? colors.success : colors.textSecondary,
                    fontWeight: typography.fontWeight.medium,
                    marginTop: 2,
                  }}>
                  {getStatusText()}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        </View>

        {/* Right Section - Action Buttons */}
        <View style={{flexDirection: 'row', alignItems: 'center'}}>
          {showCallButtons && (
            <>
              {renderActionButton(<Text style={{fontSize: 16}}>📞</Text>, onCallPress, !onCallPress)}
              {renderActionButton(<Text style={{fontSize: 16}}>📹</Text>, onVideoPress, !onVideoPress)}
            </>
          )}

          {(showBlockButton || showMoreButton || showLeaveGroupButton) && (
            <TouchableOpacity
              ref={menuButtonRef}
              onPress={handleMenuPress}
              style={{
                width: 40,
                height: 40,
                borderRadius: borderRadius.full,
                backgroundColor: colors.gray100,
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: spacing.sm,
              }}
              activeOpacity={0.7}>
              <Ionicons name="ellipsis-vertical" size={20} color={colors.textPrimary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Popup Menu */}
      <Modal
        visible={showMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowMenu(false)}>
        <TouchableOpacity
          style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
          activeOpacity={1}
          onPress={() => setShowMenu(false)}>
          <View
            style={{
              position: 'absolute',
              top: menuPosition.y,
              right: spacing.lg,
              backgroundColor: colors.surface,
              borderRadius: borderRadius.lg,
              minWidth: 150,
              ...shadows.md,
              elevation: 8,
            }}>
            {showBlockButton && (
              <TouchableOpacity
                onPress={handleBlockPress}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: spacing.md,
                  paddingVertical: spacing.sm,
                  borderBottomWidth: showMoreButton || showLeaveGroupButton ? 1 : 0,
                  borderBottomColor: colors.border,
                }}>
                <Ionicons 
                  name={isUserBlocked ? "checkmark-circle-outline" : "ban-outline"} 
                  size={18} 
                  color={isUserBlocked ? colors.success : colors.error} 
                  style={{marginRight: spacing.sm}}
                />
                <Text style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                  color: isUserBlocked ? colors.success : colors.error,
                  flex: 1,
                }}>
                  {isUserBlocked ? 'Unblock User' : 'Block User'}
                </Text>
              </TouchableOpacity>
            )}
            
            {showLeaveGroupButton && (
              <TouchableOpacity
                onPress={() => {
                  setShowMenu(false);
                  if (onLeaveGroupPress) {
                    haptics.medium();
                    onLeaveGroupPress();
                  }
                }}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: spacing.md,
                  paddingVertical: spacing.sm,
                  borderBottomWidth: showMoreButton ? 1 : 0,
                  borderBottomColor: colors.border,
                }}>
                <Ionicons 
                  name="exit-outline" 
                  size={18} 
                  color={colors.error} 
                  style={{marginRight: spacing.sm}}
                />
                <Text style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                  color: colors.error,
                  flex: 1,
                }}>
                  Leave Group
                </Text>
              </TouchableOpacity>
            )}
            
            {showMoreButton && (
              <TouchableOpacity
                onPress={() => {
                  setShowMenu(false);
                  if (onMorePress) {
                    haptics.medium();
                    onMorePress();
                  }
                }}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: spacing.md,
                  paddingVertical: spacing.sm,
                }}>
                <Ionicons 
                  name="settings-outline" 
                  size={18} 
                  color={colors.textPrimary} 
                  style={{marginRight: spacing.sm}}
                />
                <Text style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                  color: colors.textPrimary,
                  flex: 1,
                }}>
                  More Options
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

export default ModernChatHeader;
